
import axios from "axios";
import { cdn_URL, base_URL } from "../config/config";

/**
 * Exchanges stored S3 image/video URLs for short-lived presigned URLs so the
 * private fmv-data bucket can be read at original quality (used on preview
 * pages). Non-S3 URLs (YouTube, external) come back unchanged. Falls back to
 * the original URLs if the request fails so media still attempts to render.
 */
/**
 * Media-type helpers that are safe for presigned URLs. A presigned S3 URL looks
 * like `https://bucket.s3.../video.mp4?X-Amz-Signature=...`, so checking the
 * raw string with `.endsWith(".mp4")` fails — strip the query string first.
 */
export function isYouTubeUrl(url) {
  return (
    typeof url === "string" &&
    (url.includes("youtube.com") || url.includes("youtu.be"))
  );
}

export function isMp4Url(url) {
  if (typeof url !== "string") return false;
  return url.split("?")[0].toLowerCase().endsWith(".mp4");
}

export function isVideoUrl(url) {
  return isYouTubeUrl(url) || isMp4Url(url);
}

export async function getPresignedUrls(urls) {
  const list = (urls || []).filter(Boolean);
  if (list.length === 0) return [];
  try {
    const res = await axios.post(`${base_URL}/api/hotels/presign-images`, {
      urls: list,
    });
    return res?.data?.urls || list;
  } catch {
    return list;
  }
}

/**
 * Returns a CDN thumbnail URL for a given S3 image URL.
 * Uses Thumbor-style URL: 400px wide, WebP, quality 85.
 * Falls back to the original URL for non-S3 URLs or on error.
 */
export function getS3Thumb(url) {
  if (!url) return "";
  try {
    let key = url
      .replace(/^https?:\/\/[^/]+\.s3[^/]*\.amazonaws\.com\//, "")
      .replace(/^s3:\/\/[^/]+\//, "");
    if (key === url) return url; // not an S3 URL — return as-is
    // Legacy files were stored with "+"-encoded spaces (e.g. "fridge+5.png"),
    // but the real S3 key has a real space. Thumbor (the CDN) does NOT decode
    // "+" -> space and 404s, so normalize to "%20" which it serves correctly.
    // A literal "+" in a key is stored as "%2B", which this leaves intact.
    key = key.replace(/\+/g, "%20");
    return `${cdn_URL}/fit-in/400x0/filters:format(webp):quality(85)/${key}`;
  } catch {
    return url;
  }
}

export function deepClean(obj) {
  if (Array.isArray(obj)) {
    return obj
      .map(deepClean)
      .filter(
        (item) =>
          item !== undefined &&
          item !== null &&
          !(typeof item === "string" && item.trim() === "") &&
          !(Array.isArray(item) && item.length === 0) &&
          !(typeof item === "object" && Object.keys(item).length === 0)
      );
  } else if (typeof obj === "object" && obj !== null) {
    return Object.entries(obj).reduce((acc, [key, value]) => {
      const cleaned = deepClean(value);
      if (
        cleaned !== undefined &&
        cleaned !== null &&
        !(typeof cleaned === "string" && cleaned.trim() === "") &&
        !(Array.isArray(cleaned) && cleaned.length === 0) &&
        !(typeof cleaned === "object" && Object.keys(cleaned).length === 0)
      ) {
        acc[key] = cleaned;
      }
      return acc;
    }, {});
  }
  return obj;
}



export function getURLParams(filters) {
  if (!filters || Object.keys(filters).length === 0) return "";
  const cleaned = deepClean(filters);
  console.log("CLEANED FILTERS:", cleaned);
  if (Object.keys(cleaned).length === 0) return "";
  return `filters=${JSON.stringify(cleaned)}`;
}